import { useNavigate } from "@tanstack/react-router";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { Plus } from "lucide-react";
import { motion } from "motion/react";
import { type FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { PlanName, ValidationSuscriptionButton } from "@/common/enums/enums";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { Skeleton } from "@/components/ui/skeleton";
import { useOwnUsage } from "@/lib/usage-queries";
import { container, formatTitle, item, roundPercentage } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { DonutChart } from "../../components/ui/DonutChart";
import { AnalyticsMonth } from "./redesign/AnalyticsMonth";

ChartJS.register(ArcElement, Tooltip, Legend);

const ClassicDashboard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUser, user, userData } = useAuthStore();
  const usage = useOwnUsage();

  // Paid plans are unlimited: a quota, remaining share or "∞" total only
  // confuses, so they see the minutes they used and nothing else.
  const isPaidPlan =
    Boolean(userData?.planName) && userData?.planName !== PlanName.FREE_TRIAL;

  useEffect(() => {
    if (user?.email) {
      void getUser();
    }
  }, [user?.email, getUser]);

  const minutesUsed = Math.floor(
    (usage.data?.subscription.usedSeconds ?? 0) / 60,
  );
  const totalMinutes = Math.floor(
    ((usage.data?.subscription.allowanceSeconds ?? 0) +
      (usage.data?.additional.availableSeconds ?? 0)) /
      60,
  );
  const chartData = [minutesUsed, Math.max(totalMinutes - minutesUsed, 0)];
  const percentage = roundPercentage(
    totalMinutes > 0 ? (100 * minutesUsed) / totalMinutes : 0,
  );

  return (
    <Layout
      navbarTitle={t("dashboard.title")}
      breadcrumbs={[
        {
          name: t("dashboard.title"),
          href: "/analytics",
          current: true,
        },
      ]}
    >
      <div className="p-6 md:p-8 lg:p-10">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {/* Header */}
          <motion.div
            className="flex flex-col gap-2 sm:flex-row items-center justify-between"
            variants={item}
          >
            <h1 className="text-xl font-bold tracking-tight">
              {t("dashboard.hi")},{" "}
              {userData?.firstname && userData?.lastname
                ? `${formatTitle(userData.userPreference.title)}${userData.firstname} ${userData.lastname}`
                : user?.email}
            </h1>
            <SubscriptionOnlyButton
              validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
              onClick={async () => {
                await navigate({
                  to: "/visits",
                  search: { openNewVisit: true },
                });
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("dashboard.buttons.newVisit")}
            </SubscriptionOnlyButton>
          </motion.div>
          <motion.div
            className="flex flex-col gap-2 sm:flex-row items-center justify-between"
            variants={item}
          >
            {usage.error ? (
              <div role="alert">
                <p>{t("error.unknown")}</p>
                <Button
                  onClick={() => {
                    void usage.refresh();
                  }}
                >
                  {t("common.retry", "Retry")}
                </Button>
              </div>
            ) : isPaidPlan ? (
              <div
                className="mx-auto w-full max-w-xs text-center"
                data-testid="unlimited-usage"
              >
                {usage.loading || !usage.data ? (
                  <div className="space-y-1">
                    <Skeleton className="mx-auto h-10 w-20" />
                    <Skeleton className="mx-auto h-4 w-28" />
                  </div>
                ) : (
                  <>
                    <p className="m-0 text-4xl font-bold">{minutesUsed}</p>
                    <p className="m-0 text-base font-normal">
                      {t("dashboard.labels.minutesUsed")}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <DonutChart
                centerText={t("dashboard.labels.monthlyPlanUsed")}
                colors={["#0399FF", "#EAEAF2"]}
                data={chartData}
                hoverColors={["#36aeff", "#d1d5db"]}
                loading={usage.loading || !usage.data}
                percentage={percentage}
                additionalInfo={[
                  {
                    label: t("dashboard.labels.minutesUsed"),
                    value: minutesUsed,
                  },
                  {
                    label: t("dashboard.labels.totalMinutes"),
                    value: totalMinutes,
                  },
                ]}
              />
            )}
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

// v3.2 app (VITE_APP_REDESIGN_ENABLED): Analytics "Month"; classic otherwise.
const AnalyticsV32: FC = () => {
  const { t } = useTranslation();
  return (
    <Layout
      breadcrumbs={[
        { name: t("dashboard.title"), href: "/analytics", current: true },
      ]}
      navbarTitle={t("dashboard.title")}
    >
      <AnalyticsMonth />
    </Layout>
  );
};

export const Dashboard: FC = () =>
  AppConfig.enableAppRedesign ? <AnalyticsV32 /> : <ClassicDashboard />;
